<template>
  <div :class="$style.container">
    <fieldset :class="$style.fields">
      <InputValidator
        :class="$style.inputContainer"
        :config="{
          attribute: 'name',
          errorStyleExceptions: $style.hideEmptyError,
          name: nameAttributeName,
          placeholder: 'Public Key Name',
          readonly: collapsed ? true : false,
          type: 'text',
          validate: 'required',
          value: listItem.name,
        }"
        @InputValidatorModel="handleInputChange"
      />
      <TextareaValidator
        :class="[
          $style.textarea,
          collapsed ? $style.textareaCollapsed : '',
        ]"
        :config="{
          attribute: 'public_key',
          errorStyleExceptions: $style.hideEmptyError,
          name: publicKeyAttributeName,
          placeholder: 'Public Key',
          spellcheck: false,
          value: listItem.public_key,
        }"
        @InputValidatorModel="handleInputChange"
      />
    </fieldset>
    <button
      :class="$style.collapseButton"
      @click="handleCollapseButtonClick"
    >
      <CollapseCaret :collapsed="collapsed" />
    </button>
    <span :class="$style.createDate">{{ addedOn }}</span>
  </div>
</template>
<script>
import CollapseCaret from 'client/components/ContentSection/CollapseCaret.vue';
import Constants from 'client/util/Constants';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import TextareaValidator from 'client/components/FormValidation/TextareaValidator.vue';
import moment from 'moment';

const { dateFormat } = Constants;

export default {
  name: 'PublicKeyFields',

  components: {
    CollapseCaret,
    InputValidator,
    TextareaValidator,
  },

  inject: ['$validator'],

  props: {
    addingItem: {
      default: false,
      required: false,
      type: Boolean,
    },
    handleChangeListItem: {
      required: true,
      type: Function,
    },
    index: {
      default: undefined,
      required: false,
      type: Number,
    },
    listItem: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      collapsed: !(this.addingItem === true && this.index === 0),
    };
  },

  computed: {
    addedOn() {
      return this.listItem.create_date
        ? `added ${moment(this.listItem.create_date).format(dateFormat)}`
        : '';
    },

    /**
     * Create a unique name for the attribute name field so errors in this
     * field can be uniquely identified.
     */
    nameAttributeName() {
      return `attribute-${this.scope}-name`;
    },

    /**
     * Expose public key in a computed property to avoid directly mutating the
     * state.
     * @see https://vuex.vuejs.org/guide/forms.html#two-way-computed-property
     */
    publicKey: {
      get() {
        return this.listItem.public_key;
      },
      set(value) {
        this.handleInputChange(value, 'public_key');
      },
    },

    publicKeyAttributeName() {
      return `attribute-${this.scope}-publickey`;
    },

    scope() {
      return this.listItem._id || this.listItem.tempId;
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    handleCollapseButtonClick() {
      this.collapsed = !this.collapsed;
    },

    handleInputChange(value, key) {
      this.handleChangeListItem({ ...this.listItem, [key]: value });
    },

    /**
     * Use field specific custom messages in vee-validate so that the unique
     * input names are not used in the error messages.
     * @see https://baianat.github.io/vee-validate/guide/messages.html#field-specific-custom-messages
     */
    handleMounted() {
      const vValidateDict = {
        custom: {
          [this.nameAttributeName]: {
            required: '* The public key name field is required.',
          },
        },
      };

      this.$validator.localize('en', vValidateDict);
    },
  },
};
</script>
<style module>
@value (lightBlue, grey90, greyLight4) from '~css/colors.css';
@value (slideUpDownTransition) from '~css/transitions.css';

.container {
  margin-bottom: 20px;
  position: relative;
}

.fields {
  width: calc(100% - 1.5rem);
}

/*
 * TODO: Move these styles to a common location. They are ListEditor button styles.
 */
.collapseButton {
  appearance: none;
  background-color: transparent;
  border: 0 none !important;
  color: greyLight4;
  margin-left: 0.5rem;
  padding: unset;
  position: absolute;
  right: 0;
  top: 5px;
  width: 1rem;
}

.collapseButton:focus {
  outline: none;
}

.inputContainer {
  composes: inputContainer from "~components/FormValidation/css/FormValidationFields.css";
  border-bottom: 3px solid grey90;
  padding-bottom: 8px;
  vertical-align: top;
}

.hideEmptyError {
  min-height: 0;
}

.textarea {
  display: block;
  max-height: 120px;
  overflow: hidden;
  transition: slideUpDownTransition;
  width: 100%;
}

.textareaCollapsed {
  border: 0;
  max-height: 0;
  padding: 0;
}

.createDate {
  color: lightBlue;
  font-size: 0.65rem;
  padding-left: 5px;
}
</style>
